import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@infrastructure/supabase/SupabaseConfig';
import { identityDomain } from '../domain/identity/IdentityDomainFactory';
import { USER_ROLE, STORAGE_KEYS } from '@core/constants';
import { UserSession } from '../domain/identity/entities/UserSession';

export interface AuthState {
  user: any | null;
  session: UserSession | null;
  loading: boolean;
  role: string | null;
  ownerId: string | null;
  brandInfo: any | null;
  offlineMode: boolean;
  error: string | null;
  bootstrapRequired: boolean;
  isGhostMode: boolean;
  originalRole: string | null;
}

/**
 * useAuth Hook - Clean Architecture Implementation
 * Centralizes authentication and role-based session management using Domain Layer
 */
export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    role: null,
    ownerId: null,
    brandInfo: null,
    offlineMode: false,
    error: null,
    bootstrapRequired: false,
    isGhostMode: false,
    originalRole: null,
  });

  useEffect(() => {
    // 1. Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      handleAuthChange(session?.user ?? null);
    });

    // 2. Initial check
    supabase.auth.getUser().then(({ data: { user } }) => {
      handleAuthChange(user);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleAuthChange = async (supabaseUser: any) => {
    if (!supabaseUser) {
      setState(prev => ({ 
        ...prev, 
        user: null, session: null, role: null, ownerId: null, brandInfo: null,
        loading: false, bootstrapRequired: false 
      }));
      return;
    }

    try {
      const getSession = identityDomain.getGetSessionUseCase();
      const userSession = await getSession.execute(
        supabaseUser.id, 
        supabaseUser.email || '', 
        supabaseUser.user_metadata
      );

      setState(prev => ({
        ...prev,
        user: supabaseUser,
        session: userSession,
        role: (userSession.role as any).role,
        ownerId: userSession.merchantId || null,
        brandInfo: userSession.displayName, // Mapping needs to be more robust later
        bootstrapRequired: userSession.role.role === null,
        loading: false
      }));

    } catch (err: any) {
      console.error("Auth session recovery error:", err);
      setState(prev => ({ ...prev, error: err.message, loading: false }));
    }
  };

  const handleLogout = async () => {
    await identityDomain.authPort.logout();
    localStorage.removeItem(STORAGE_KEYS.OWNER_ID);
    localStorage.removeItem(STORAGE_KEYS.ROLE);
  };

  const enterGhostMode = useCallback(async (targetOwnerId: string) => {
    if (state.role !== USER_ROLE.MASTER && state.originalRole !== USER_ROLE.MASTER) return;
    
    await supabase.from('audit_logs').insert({
      actor_id: state.user?.id,
      target_id: targetOwnerId,
      action: 'ENTER_GHOST_MODE'
    });

    setState(prev => ({
      ...prev,
      ownerId: targetOwnerId,
      isGhostMode: true,
      originalRole: prev.originalRole || prev.role,
      role: USER_ROLE.OWNER
    }));
  }, [state.user, state.role, state.originalRole]);

  const exitGhostMode = useCallback(() => {
    setState(prev => ({
      ...prev,
      ownerId: prev.user?.id || null,
      role: prev.originalRole,
      isGhostMode: false,
      originalRole: null
    }));
  }, []);

  return {
    ...state,
    logout: handleLogout,
    enterGhostMode,
    exitGhostMode,
    refreshBootstrapStatus: () => window.location.reload()
  };
};

export default useAuth;
